import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  BreadcrumbGroup,
  Alert,
  Spinner,
  KeyValuePairs,
  StatusIndicator,
  Badge,
  TokenGroup,
  ColumnLayout,
  FormField,
  Box,
  Table,
  Flashbar,
  TextContent,
  Modal,
} from '@cloudscape-design/components';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { checkFeaturePermission, formatDateTime } from 'src/utils/generalUtils';
import { replaceMediaIdsWithPresignedUrlsInSubQuestions } from 'src/utils/mediaUtils';
import useUserProfile from 'src/hooks/useUserProfile';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';
import { useUsers } from 'src/store/users/useUsers';
import { useQuestion } from 'src/store/question/useQuestion';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import Divider from 'src/components/divider/Divider';
import SafeHtml from 'src/components/SafeHtml';
import TextEditor from 'src/components/textEditor/TextEditor';
import { FileMetadata } from 'src/components/FileDisplay';
import { useBaseHook } from 'src/hooks/useBaseHook';
import { MediaData } from 'src/types/helios/Media';
import { DEFAULT_LANGUAGE } from 'src/contstant/commonLabels';
import routePaths from 'src/contstant/routePaths';
import {
  getAlLMediaResponseList,
  getQuestionContent,
  openQuestionEditPage,
  getDocumentMetadata,
  getSubQuestionDocumentMetadata,
} from 'src/helper/questionHelper';
import { ResponseType } from 'src/types/helios/Question';
import {
  canEditTranslation,
  hasExistingTranslation,
  openTranslationEditPage,
} from 'src/helper/translationHelper';
import { LanguageDropdown } from 'src/components/LanguageDropdown';
import { useFeatureFlags } from 'src/context/FeatureFlagsContext';
import TranslationStalenessAlert from 'src/components/question/translation/TranslationStalenessAlert';

interface EnabledDisabledIndicatorProps {
  enabled?: boolean;
}

const EnabledDisabledIndicator: React.FC<EnabledDisabledIndicatorProps> = ({
  enabled,
}) => (
  <StatusIndicator type={enabled ? 'success' : 'stopped'}>
    {enabled ? 'Enabled' : 'Disabled'}
  </StatusIndicator>
);

const QuestionDetailsPage = () => {
  const navigate = useNavigate();
  const basehook = useBaseHook();
  const [searchParams, setSearchParams] = useSearchParams();
  const programId = searchParams.get('programId');
  const questionId = searchParams.get('questionId');
  const division = searchParams.get('division');
  const version = searchParams.get('version');
  const businessUnit = searchParams.get('businessUnit');
  const [translationError, setTranslationError] = useState('');
  const { enableEnhancedResponseTypes } = useFeatureFlags();
  const {
    question,
    loadQuestionById,
    getQuestionByIdApiStatus,
    clearQuestion,
    updateQuestion,
  } = useQuestion();

  const {
    getUserBusinessUnitId,
    getPersona,
    getUserId,
    getUserProgramId,
    getDivisions,
    getUserSelectedLanguage,
    selectUserLanguage,
    getPreferLanguageList,
  } = useUserProfile();

  const { businessUnitList } = useBusinessUnit();

  const getBusinessUnitName = (businessUnitId: string) => {
    const businessUnit = businessUnitList.find(
      (bu) => bu.businessUnitId === businessUnitId,
    );
    return businessUnit ? businessUnit.name : businessUnitId;
  };

  const { checkUserPermissions, userPermission, currentUser } = useUsers();

  const isEditQuestion = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionAccessConfig.QUESTION.EDIT_QUESTION,
      userPermission?.permissions,
    );
  }, [userPermission?.permissions]);

  useEffect(() => {
    checkUserPermissions({
      userId: getUserId(),
      programId: getUserProgramId(),
      businessUnit: getUserBusinessUnitId(),
      permissions: [permissionAccessConfig.QUESTION.EDIT_QUESTION],
      divisions: getDivisions(),
    });
  }, [getUserBusinessUnitId(), currentUser]);

  const translatedContent = useMemo(() => {
    if (!question) return null;

    const content = getQuestionContent(question, getUserSelectedLanguage(), (error) =>
      setTranslationError(getQuestionByIdApiStatus.isLoading === false ? error : ''),
    );

    // Replace media IDs with presigned URLs in subQuestions for display
    if (content?.subQuestions && question.subQuestions) {
      // Map translated subQuestions with original media responses for URL replacement
      const subQuestionsWithMediaResponses = content.subQuestions.map(
        (subQuestion, index) => {
          const originalSubQuestion = question.subQuestions?.[index];
          if (!originalSubQuestion) return subQuestion;

          // Attach media responses from original question to translated content
          return {
            ...subQuestion,
            mediaResponses: originalSubQuestion.mediaResponses,
            mediaResponsesMap: originalSubQuestion.mediaResponsesMap,
          };
        },
      );

      // Use utility function to replace media IDs with presigned URLs
      content.subQuestions = replaceMediaIdsWithPresignedUrlsInSubQuestions(
        subQuestionsWithMediaResponses,
      );
    }

    return content;
  }, [question, getUserSelectedLanguage(), getQuestionByIdApiStatus.isLoading]);

  const allMediaResonses = useMemo(
    () => (question ? getAlLMediaResponseList(question) : []),
    [question],
  );

  useEffect(() => {
    if (questionId && programId && division && businessUnit) {
      const params = {
        programId: programId,
        questionId: questionId,
        divisionName: division,
        businessUnit: businessUnit,
        language: getUserSelectedLanguage(),
        ...(version && { version }),
      };
      loadQuestionById(params);
    } else {
      navigate(routePaths.QUESTION.path, { replace: true });
    }
    return () => {
      clearQuestion();
    };
  }, [getUserSelectedLanguage()]);
  const renderQuestionlDetails = () => {
    const extraData = question
      ? [
          {
            label: 'Question ID',
            value: <Badge color="blue">{question.questionId}</Badge>,
          },
          { label: 'Version', value: question.version },
          { label: 'Business Unit', value: getBusinessUnitName(question.businessUnit) },
          { label: 'Division Name', value: question.divisionName },
          { label: 'Category Name', value: question.categories?.join(', ') },
          {
            label: 'AuditScore',
            value: <Badge color="severity-low">{question.auditScore}</Badge>,
          },
          {
            label: 'Status',
            value: (
              <StatusIndicator type={question.isActive ? 'success' : 'error'}>
                {question.isActive ? 'Active' : 'Inactive'}
              </StatusIndicator>
            ),
          },
          {
            label: 'Comment Required on Fail',
            value: <EnabledDisabledIndicator enabled={question.requireCommentOnFail} />,
          },
          {
            label: 'Attachment Required on Fail',
            value: <EnabledDisabledIndicator enabled={question.requireEvidenceOnFail} />,
          },
          {
            label: 'Comment Required on Pass',
            value: <EnabledDisabledIndicator enabled={question.requireCommentOnPass} />,
          },
          {
            label: 'Attachment Required on Pass',
            value: <EnabledDisabledIndicator enabled={question.requireEvidenceOnPass} />,
          },
          { label: 'Created At', value: formatDateTime(question.createdAt || '') },
          {
            label: 'Last Updated At',
            value: formatDateTime(question.lastUpdatedAt || ''),
          },
          { label: 'Last Updated By', value: question.lastUpdatedBy },
          { label: 'Created By', value: question.createdBy },
          { label: 'Translation Status', value: question.translationStatus || '-' },
          ...(question.locationsExcluded && question.locationsExcluded.length > 0
            ? [
                {
                  label: 'Locations Excluded',
                  value: (
                    <div className="location-container">
                      {question.locationsExcluded.map((item) => (
                        <span key={item} className="location">
                          {item}
                        </span>
                      ))}
                    </div>
                  ),
                },
              ]
            : []),
          ...(question.tagsIncluded && Object.keys(question.tagsIncluded).length > 0
            ? [
                {
                  label: 'Tags Included',
                  value: (
                    <div className="tag-container">
                      {Object.entries(question.tagsIncluded).map(
                        ([tagGroupName, tagValues]) => {
                          return tagValues.map((data, index) => {
                            const sanitizedKey =
                              `${tagGroupName}-${data}-${index}`.replace(
                                /[^a-zA-Z0-9-]/g,
                                '-',
                              );
                            return (
                              <span key={sanitizedKey} className="tag">
                                {tagGroupName} - {data}
                              </span>
                            );
                          });
                        },
                      )}
                    </div>
                  ),
                },
              ]
            : []),
          ...(question.tagsExcluded && Object.keys(question.tagsExcluded).length > 0
            ? [
                {
                  label: 'Tags Excluded',
                  value: (
                    <div className="tag-container">
                      {Object.entries(question.tagsExcluded).map(
                        ([tagGroupName, tagValues]) => {
                          return tagValues.map((data, index) => {
                            const sanitizedKey =
                              `${tagGroupName}-${data}-${index}`.replace(
                                /[^a-zA-Z0-9-]/g,
                                '-',
                              );
                            return (
                              <span key={sanitizedKey} className="tag">
                                {tagGroupName} - {data}
                              </span>
                            );
                          });
                        },
                      )}
                    </div>
                  ),
                },
              ]
            : []),
        ]
      : [];

    return (
      <SpaceBetween size="l">
        <Container
          header={
            <Header
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <LanguageDropdown
                    selectedLanguage={getUserSelectedLanguage()}
                    defaultLanguage={DEFAULT_LANGUAGE}
                    filterLanguges={getPreferLanguageList()}
                    onLanguageChange={(value) => {
                      selectUserLanguage(value);
                    }}
                  />
                  {isEditQuestion() && question && (
                    <Button
                      iconName="copy"
                      onClick={() => {
                        openQuestionEditPage(navigate, true, question);
                      }}
                    >
                      Copy
                    </Button>
                  )}
                  {isEditQuestion() && question && canEditTranslation(question) && (
                    <Button
                      variant="primary"
                      onClick={() => {
                        // Use question-edit for default language, translation-edit for others
                        if (getUserSelectedLanguage() === question.defaultLanguage) {
                          openQuestionEditPage(navigate, false, question);
                        } else {
                          openTranslationEditPage(
                            navigate,
                            question,
                            getUserSelectedLanguage(),
                          );
                        }
                      }}
                    >
                      {getUserSelectedLanguage() === question.defaultLanguage
                        ? 'Edit'
                        : hasExistingTranslation(question, getUserSelectedLanguage())
                          ? 'Edit Translation'
                          : 'Add Translation'}
                    </Button>
                  )}
                </SpaceBetween>
              }
              variant="h3"
            >
              Basic Info
            </Header>
          }
        >
          {question && <KeyValuePairs columns={3} items={extraData} />}
        </Container>
        {translatedContent &&
          [
            {
              key: 'questionText',
              title: 'Question',
              content: translatedContent.questionDetails.questionText,
              showTitle: true,
              mediaList: question?.mediaResponses?.questionText || [],
            },
            {
              key: 'auditInstruction',
              title: 'Audit Instruction',
              content: translatedContent.questionDetails.auditInstruction,
              mediaList: question?.mediaResponses?.auditInstruction || [],
            },
            {
              key: 'passCriteria',
              title: 'Pass Criteria',
              content: translatedContent.questionDetails.passCriteria,
              mediaList: question?.mediaResponses?.passCriteria || [],
            },
            {
              key: 'failCriteria',
              title: 'Fail Criteria',
              content: translatedContent.questionDetails.failCriteria,
              mediaList: question?.mediaResponses?.failCriteria || [],
            },
            {
              key: 'justification',
              title: 'Justification',
              content: translatedContent.questionDetails.justification,
              mediaList: question?.mediaResponses?.justification || [],
            },
          ].map(
            ({ key, title, content, showTitle, mediaList }) =>
              content && (
                <Container key={key}>
                  <SpaceBetween size="s">
                    <TextContent>
                      <h4>{title}</h4>
                      {showTitle && <h5>{translatedContent.questionDetails.title}</h5>}
                    </TextContent>
                    <TextEditor
                      key={`${key}-${getUserSelectedLanguage()}`}
                      value={content}
                      onChange={() => {}}
                      readOnly={true}
                      mediaResponse={mediaList as any}
                      initialFileMetadata={
                        question ? getDocumentMetadata(question, key as any) : []
                      }
                    />
                  </SpaceBetween>
                </Container>
              ),
          )}
        {translatedContent?.taskDetails &&
          (translatedContent.taskDetails.taskName?.trim() ||
            translatedContent.taskDetails.taskDescription?.trim()) && (
            <Container>
              <TextContent>
                <h4>Default Task Details</h4>
              </TextContent>
              {/* Task Duration (Days) was intentionally removed from display:
                  it no longer drives task creation (operators must enter it
                  fresh each time), so showing a stored value here would
                  mislead question authors into thinking it applies. */}
              <SpaceBetween size="l">
                <FormField label="Task Name" stretch>
                  <Box variant="p">{translatedContent.taskDetails?.taskName}</Box>
                </FormField>
                <TextEditor
                  key={`taskDescription-${getUserSelectedLanguage()}`}
                  value={translatedContent.taskDetails?.taskDescription || ''}
                  onChange={() => {}}
                  readOnly={true}
                  mediaResponse={question?.mediaResponses?.taskDescription as any}
                  initialFileMetadata={
                    question ? getDocumentMetadata(question, 'taskDescription') : []
                  }
                />
              </SpaceBetween>
            </Container>
          )}

        {question && question.docLinks && question.docLinks.length > 0 && (
          <Container>
            <TextContent>
              <h4>Standard Operating Procedures</h4>
            </TextContent>
            <Table
              variant="borderless"
              columnDefinitions={[
                { id: 'title', header: 'Title', cell: (item) => item.title },
                {
                  id: 'link',
                  header: 'Link',
                  cell: (item) => (
                    <a href={item.link} target="_blank" rel="noopener noreferrer">
                      {item.link}
                    </a>
                  ),
                },
              ]}
              items={question.docLinks}
            />
          </Container>
        )}

        {translatedContent?.subQuestions && translatedContent.subQuestions.length > 0 && (
          <Container>
            <TextContent>
              <h4>Sub Questions</h4>
            </TextContent>
            {enableEnhancedResponseTypes && (
              <Box margin={{ bottom: 's' }}>
                {question?.hasConditionalSubQuestions ? (
                  <>
                    <Badge color="grey">Conditional</Badge>{' '}
                    <Box variant="small" display="inline" color="text-body-secondary">
                      Sub-questions appear based on the parent answer (Pass/Fail)
                    </Box>
                  </>
                ) : (
                  <>
                    <Badge color="grey">Unconditional</Badge>{' '}
                    <Box variant="small" display="inline" color="text-body-secondary">
                      All sub-questions always appear during the audit
                    </Box>
                  </>
                )}
              </Box>
            )}
            <SpaceBetween size="l">
              {translatedContent.subQuestions.map((subQuestion, index) => {
                const renderTaskDetails = (taskDetails: any) => (
                  <>
                    <Divider />
                    {/* Task Duration (Days) intentionally removed — see note
                        on the main-question block above. */}
                    <SpaceBetween size="l">
                      <FormField label="Task Name" stretch>
                        <Box variant="p">{taskDetails.taskName}</Box>
                      </FormField>
                      <FormField label="Task Description" stretch>
                        <TextEditor
                          key={`subquestion-taskdesc-${index}-${getUserSelectedLanguage()}`}
                          value={taskDetails.taskDescription || ''}
                          onChange={() => {}}
                          readOnly={true}
                          mediaResponse={
                            (question?.subQuestions?.[index]?.mediaResponsesMap
                              ?.taskDescription ||
                              question?.subQuestions?.[index]?.mediaResponses) as any
                          }
                          initialFileMetadata={
                            question?.subQuestions?.[index]
                              ? getSubQuestionDocumentMetadata(
                                  question.subQuestions[index],
                                  'taskDescription',
                                )
                              : []
                          }
                        />
                      </FormField>
                    </SpaceBetween>
                  </>
                );

                return (
                  <Container key={index}>
                    <SpaceBetween size="s">
                      <TextContent>
                        <p>SubQuestion ID: {subQuestion.subQuestionId}</p>
                        <h4>{subQuestion.title}</h4>
                      </TextContent>
                      <TextEditor
                        key={`subquestion-instruction-${index}-${getUserSelectedLanguage()}`}
                        value={subQuestion.instruction}
                        onChange={() => {}}
                        readOnly={true}
                        mediaResponse={
                          (question?.subQuestions?.[index]?.mediaResponsesMap
                            ?.instruction ||
                            question?.subQuestions?.[index]?.mediaResponses) as any
                        }
                        initialFileMetadata={
                          question?.subQuestions?.[index]
                            ? getSubQuestionDocumentMetadata(
                                question.subQuestions[index],
                                'instruction',
                              )
                            : []
                        }
                      />
                      {/* ERT: Show response type, show on, options for data-collection subs */}
                      {enableEnhancedResponseTypes &&
                        question?.subQuestions?.[index]?.responseType && (
                          <ColumnLayout columns={2}>
                            <Box>
                              <Box variant="awsui-key-label">Response Type</Box>
                              {(() => {
                                const labels: Record<string, string> = {
                                  PASS_FAIL: 'Pass / Fail',
                                  TEXT: 'Text Input',
                                  NUMERIC: 'Numeric Input',
                                  SINGLE_SELECT: 'Single Select',
                                  MULTI_SELECT: 'Multi Select',
                                  YES_NO: 'Yes / No',
                                  USER_ALIAS: 'Employee Login',
                                };
                                return (
                                  labels[
                                    question.subQuestions[index].responseType || ''
                                  ] || question.subQuestions[index].responseType
                                );
                              })()}
                            </Box>
                            {question.hasConditionalSubQuestions &&
                              question.subQuestions[index].showWhenParentIs && (
                                <Box>
                                  <Box variant="awsui-key-label">Show When Parent Is</Box>
                                  {question.subQuestions[index].showWhenParentIs ===
                                  'BOTH'
                                    ? 'Both (Pass or Fail)'
                                    : question.subQuestions[index].showWhenParentIs}
                                </Box>
                              )}
                          </ColumnLayout>
                        )}
                      {/* ERT: Show options for select types */}
                      {enableEnhancedResponseTypes &&
                        question?.subQuestions?.[index]?.options &&
                        question.subQuestions[index].options!.length > 0 && (
                          <Box>
                            <Box variant="awsui-key-label">Configured Values</Box>
                            <TokenGroup
                              items={question.subQuestions[index].options!.map((opt) => ({
                                label: opt,
                              }))}
                              readOnly
                            />
                          </Box>
                        )}
                      {/* Toggles: show unconditional toggles for data-collection (ERT ON), otherwise P/F toggles */}
                      {enableEnhancedResponseTypes &&
                      question?.subQuestions?.[index]?.responseType &&
                      question.subQuestions[index].responseType !==
                        ResponseType.PASS_FAIL ? (
                        <ColumnLayout columns={2}>
                          <Box>
                            <Box variant="awsui-key-label">Comment Mandatory</Box>
                            <EnabledDisabledIndicator
                              enabled={question.subQuestions[index].requireComment}
                            />
                          </Box>
                          <Box>
                            <Box variant="awsui-key-label">Attachment Mandatory</Box>
                            <EnabledDisabledIndicator
                              enabled={question.subQuestions[index].requireAttachment}
                            />
                          </Box>
                        </ColumnLayout>
                      ) : null}
                      {(!enableEnhancedResponseTypes ||
                        !question?.subQuestions?.[index]?.responseType ||
                        question.subQuestions[index].responseType ===
                          ResponseType.PASS_FAIL) && (
                        <ColumnLayout columns={2}>
                          <Box>
                            <Box variant="awsui-key-label">Comment Required on Fail</Box>
                            <EnabledDisabledIndicator
                              enabled={
                                question?.subQuestions?.[index]?.requireCommentOnFail
                              }
                            />
                          </Box>
                          <Box>
                            <Box variant="awsui-key-label">
                              Attachment Required on Fail
                            </Box>
                            <EnabledDisabledIndicator
                              enabled={
                                question?.subQuestions?.[index]?.requireEvidenceOnFail
                              }
                            />
                          </Box>
                          <Box>
                            <Box variant="awsui-key-label">Comment Required on Pass</Box>
                            <EnabledDisabledIndicator
                              enabled={
                                question?.subQuestions?.[index]?.requireCommentOnPass
                              }
                            />
                          </Box>
                          <Box>
                            <Box variant="awsui-key-label">
                              Attachment Required on Pass
                            </Box>
                            <EnabledDisabledIndicator
                              enabled={
                                question?.subQuestions?.[index]?.requireEvidenceOnPass
                              }
                            />
                          </Box>
                        </ColumnLayout>
                      )}
                      {subQuestion.taskDetails &&
                        (subQuestion.taskDetails.taskName?.trim() ||
                          subQuestion.taskDetails.taskDescription?.trim()) &&
                        renderTaskDetails(subQuestion.taskDetails)}
                    </SpaceBetween>
                  </Container>
                );
              })}
            </SpaceBetween>
          </Container>
        )}

        {question && question.changeLog && question.changeLog.length > 0 && (
          <Container>
            <TextContent>
              <h4>Change Log</h4>
            </TextContent>
            <Table
              columnDefinitions={[
                {
                  id: 'version',
                  header: 'Version',
                  cell: (item) => <Badge color="severity-medium">v{item.version}</Badge>,
                },
                {
                  id: 'updatedAt',
                  header: 'Updated At',
                  cell: (item) => formatDateTime(item.changedAt || ''),
                },
                {
                  id: 'updatedBy',
                  header: 'Updated By',
                  cell: (item) => item.changedBy,
                },
                {
                  id: 'reason',
                  header: 'Reason',
                  cell: (item) => item.reason,
                },
              ]}
              items={question.changeLog}
              variant="borderless"
              sortingDisabled
            />
          </Container>
        )}
      </SpaceBetween>
    );
  };

  if (getQuestionByIdApiStatus.isLoading) {
    return (
      <Flashbar
        items={[
          {
            type: 'info',
            loading: true,
            header: 'Loading in Progress',
            content: 'Loading question details. This may take a few moments.',
            id: 'loading',
          },
        ]}
      />
    );
  }
  return (
    <Box margin={{ top: 's' }}>
      <SpaceBetween size="l">
        {' '}
        {getQuestionByIdApiStatus.errorMessage && (
          <Alert type="error">Error : {getQuestionByIdApiStatus.errorMessage}</Alert>
        )}
        {translationError && !getQuestionByIdApiStatus.errorMessage && (
          <Alert type="warning">
            {translationError}
            {question &&
              canEditTranslation(question) &&
              ' You can add one using the "Add Translation" button.'}
          </Alert>
        )}
        {/* Alert for translation view when viewing an overwritten translation that needs review */}
        {question && getUserSelectedLanguage() !== question.defaultLanguage && (
          <TranslationStalenessAlert
            question={question}
            selectedLanguage={getUserSelectedLanguage()}
          />
        )}
        {renderQuestionlDetails()}
      </SpaceBetween>
    </Box>
  );
};

export default QuestionDetailsPage;
